import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import { deserializeResource, serializeResource } from "permissions/_schemas/permission-schema";
import DocumentAccessSchema from "permissions/resources/documents/_schemas/document-access-schema";
import DocumentContentsAccessSchema from "permissions/resources/documents/_schemas/document-contents-access-schema";
import GeneralDocumentAccessSchema from "permissions/resources/documents/_schemas/general-document-access-schema";
import KnowledgeBaseAccessSchema from "permissions/resources/knowledge-base/knowledge-base-access-schema";
import EntityAgreementAccessSchema from "permissions/resources/legal-entities/_schemas/entity-agreement-access-schema";
import LegalEntitiesPlatformAccessSchema from "permissions/resources/legal-entities/_schemas/legal-entities-platform-access-schema";
import LegalEntityAccessSchema from "permissions/resources/legal-entities/_schemas/legal-entity-access-schema";
import MarketplaceAccessSchema from "permissions/resources/marketplace/_schema/marketplace-access-schema";
import MarketplaceSearchAccessSchema from "permissions/resources/marketplace/_schema/marketplace-search-access-schema";
import CommunicationsAccessSchema from "permissions/resources/messaging/_schemas/communications-access-schema";
import ForumAccessSchema from "permissions/resources/messaging/_schemas/forum-access-schema";
import NotificationsAccessSchema from "permissions/resources/notifications/notifications-permissions-resources-schema";
import LoginAccessSchema from "permissions/resources/users/_schemas/login-access-schema";
import UserNetworkingAccessSchema from "permissions/resources/users/_schemas/user-networking-access-schema";
import ContractsAccessSchema from "permissions/resources/work/_schemas/contracts-access-schema";
import CurrencyFieldsAccessSchema from "permissions/resources/work/_schemas/currency-fields-access-schema";
import DiscountsAccessSchema from "permissions/resources/work/_schemas/discounts-access-schema";
import FeeScheduleTemplateAccessSchema from "permissions/resources/work/_schemas/fee-schedule-template-access-schema";
import FeeSchedulesAccessSchema from "permissions/resources/work/_schemas/fee-schedules-access-schema";
import InvoicesAccessSchema from "permissions/resources/work/_schemas/invoices-access-schema";
import ProposalAccessSchema from "permissions/resources/work/_schemas/proposal-access-schema";
import ProposalRequestAccessSchema from "permissions/resources/work/_schemas/proposal-request-access-schema";
import ProposalsAccessSchema from "permissions/resources/work/_schemas/proposals-access-schema";
import TeamTemplateAccessSchema from "permissions/resources/work/_schemas/team-template-access-schema";
import TeamsAccessSchema from "permissions/resources/work/_schemas/teams-access-schema";
import WorkAgreementAccessSchema from "permissions/resources/work/_schemas/work-agreement-access-schema";
import WorkAgreementsAccessSchema from "permissions/resources/work/_schemas/work-agreements-access-schema";
import { z } from "zod";

export type PermissionTemplateJSON = z.infer<typeof PermissionTemplateSchema.PermissionTemplateJSONZ>;
export type PermissionTemplateParams = z.infer<typeof PermissionTemplateSchema.PermissionZ>;

export default class PermissionTemplateSchema {
  public static readonly PermissionTemplateJSONZ = z.object({
    id: z.uuid(),
    userId: z.uuid(),
    resources: z.array(
      GeneralDocumentAccessSchema.GeneralDocumentAccessJSONZ.or(
        DocumentContentsAccessSchema.DocumentContentsAccessJSONZ
      )
        .or(DocumentAccessSchema.DocumentAccessJSONZ)
        .or(KnowledgeBaseAccessSchema.KnowledgeBaseAccessJSONZ)
        .or(LegalEntitiesPlatformAccessSchema.LegalEntitiesPlatformAccessJSONZ)
        .or(EntityAgreementAccessSchema.EntityAgreementAccessJSONZ)
        .or(LegalEntityAccessSchema.LegalEntityAccessJSONZ)
        .or(MarketplaceAccessSchema.MarketplaceAccessJSONZ)
        .or(MarketplaceSearchAccessSchema.MarketplaceSearchAccessJSONZ)
        .or(CommunicationsAccessSchema.CommunicationsAccessJSONZ)
        .or(ForumAccessSchema.ForumAccessJSONZ)
        .or(NotificationsAccessSchema.NotificationsAccessJSONZ)
        .or(LoginAccessSchema.LoginAccessJSONZ)
        .or(UserNetworkingAccessSchema.UserNetworkingAccessJSONZ)
        .or(WorkAgreementsAccessSchema.WorkAgreementsAccessJSONZ) //
        .or(ProposalsAccessSchema.ProposalsAccessJSONZ)
        .or(TeamsAccessSchema.TeamsAccessJSONZ)
        .or(ContractsAccessSchema.ContractsAccessJSONZ)
        .or(FeeSchedulesAccessSchema.FeeSchedulesAccessJSONZ)
        .or(CurrencyFieldsAccessSchema.CurrencyFieldsAccessJSONZ)
        .or(DiscountsAccessSchema.DiscountsAccessJSONZ)
        .or(InvoicesAccessSchema.InvoicesAccessJSONZ)
        .or(ProposalAccessSchema.ProposalAccessJSONZ)
        .or(WorkAgreementAccessSchema.WorkAgreementAccessJSONZ)
        .or(ProposalRequestAccessSchema.ProposalRequestAccessJSONZ)
        .or(FeeScheduleTemplateAccessSchema.FeeScheduleTemplateAccessJSONZ)
        .or(TeamTemplateAccessSchema.TeamTemplateAccessJSONZ)
    ),
    name: z.string(),
    description: z.string().nullish(),
    createdDate: z.iso.datetime({ offset: true }).nullish(),
    deletedDate: z.iso.datetime({ offset: true }).nullish(),
  });

  public static readonly PermissionZ = z.object({
    id: z.instanceof(Guid),
    userId: z.instanceof(Guid),
    resources: z.array(
      GeneralDocumentAccessSchema.GeneralDocumentAccessZ.or(DocumentContentsAccessSchema.DocumentContentsAccessZ)
        .or(DocumentAccessSchema.DocumentAccessZ)
        .or(KnowledgeBaseAccessSchema.KnowledgeBaseAccessZ)
        .or(LegalEntitiesPlatformAccessSchema.LegalEntitiesPlatformAccessZ)
        .or(EntityAgreementAccessSchema.EntityAgreementAccessZ)
        .or(LegalEntityAccessSchema.LegalEntityAccessZ)
        .or(MarketplaceAccessSchema.MarketplaceAccessZ)
        .or(MarketplaceSearchAccessSchema.MarketplaceSearchAccessZ)
        .or(CommunicationsAccessSchema.CommunicationsAccessZ)
        .or(ForumAccessSchema.ForumAccessZ)
        .or(NotificationsAccessSchema.NotificationsAccessZ)
        .or(LoginAccessSchema.LoginAccessZ)
        .or(UserNetworkingAccessSchema.UserNetworkingAccessZ)
        .or(WorkAgreementsAccessSchema.WorkAgreementsAccessZ)
        .or(ProposalsAccessSchema.ProposalsAccessZ)
        .or(TeamsAccessSchema.TeamsAccessZ)
        .or(ContractsAccessSchema.ContractsAccessZ)
        .or(FeeSchedulesAccessSchema.FeeSchedulesAccessZ)
        .or(CurrencyFieldsAccessSchema.CurrencyFieldsAccessZ)
        .or(DiscountsAccessSchema.DiscountsAccessZ)
        .or(InvoicesAccessSchema.InvoicesAccessZ)
        .or(ProposalAccessSchema.ProposalAccessZ)
        .or(WorkAgreementAccessSchema.WorkAgreementAccessZ)
        .or(ProposalRequestAccessSchema.ProposalRequestAccessZ)
        .or(FeeScheduleTemplateAccessSchema.FeeScheduleTemplateAccessZ)
        .or(TeamTemplateAccessSchema.TeamTemplateAccessZ)
    ),
    name: z.string(),
    description: z.string().optional(),
    createdDate: DateSchema.type.optional(),
    deletedDate: DateSchema.type.optional(),
  });

  public static validate(values: unknown): PermissionTemplateParams {
    return parseOrThrow(PermissionTemplateSchema.PermissionZ, values, {
      logError: true,
      schemaName: "PermissionTemplateParams",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): PermissionTemplateJSON {
    return parseOrThrow(PermissionTemplateSchema.PermissionTemplateJSONZ, values, {
      logError: true,
      schemaName: "PermissionTemplateJSON",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is PermissionTemplateParams {
    try {
      PermissionTemplateSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is PermissionTemplateJSON {
    try {
      PermissionTemplateSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: PermissionTemplateJSON): PermissionTemplateParams {
    const parsed = parseOrThrow(PermissionTemplateSchema.PermissionTemplateJSONZ, data, {
      logError: true,
      schemaName: "PermissionTemplateJSON",
      operation: "deserialize",
      payloadSample: data,
    });
    return PermissionTemplateSchema.codec.decode(parsed);
  }

  public static serialize(notification: unknown): PermissionTemplateJSON {
    const parsed = PermissionTemplateSchema.validate(notification);
    return PermissionTemplateSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    PermissionTemplateSchema.PermissionTemplateJSONZ,
    PermissionTemplateSchema.PermissionZ,
    {
      decode: (data) => ({
        id: new Guid(GuidSchema.deserialize(data.id)),
        userId: new Guid(GuidSchema.deserialize(data.userId)),
        resources: data.resources.map((resourceJson) => {
          return deserializeResource(resourceJson) as z.infer<typeof PermissionTemplateSchema.PermissionZ.shape.resources.element>;
        }),
        name: data.name,
        description: data.description ?? undefined,
        createdDate: AHDate.fromJSON(data.createdDate) ?? undefined,
        deletedDate: AHDate.fromJSON(data.deletedDate) ?? undefined,
      }),
      encode: (template) => ({
        id: template.id.toJSON(),
        userId: template.userId.toJSON(),
        resources: template.resources.map(
          (resource) =>
            serializeResource(resource) as z.infer<typeof PermissionTemplateSchema.PermissionTemplateJSONZ.shape.resources.element>
        ),
        name: template.name,
        description: template.description,
        createdDate: template.createdDate?.toJSON(),
        deletedDate: template.deletedDate?.toJSON(),
      }),
    }
  );
}
